import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TokenStack from './TokenStack';
import TokenIcon from './TokenIcon';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import AprHistoryChart from './AprHistoryChart';
import ShareButton from './ShareButton';
import { PROTOCOL_COLORS, RISK_MAP } from '../data';
import { fmt } from '../utils';
import { getPoolUrl, getSwapUrl } from '../poolUrls';
import { useTheme } from '../theme';
import { useAlerts } from '../contexts/AlertsContext';
import { useIsMobile } from '../useMediaQuery';
import { HiOutlineArrowLeft, HiOutlineBell } from 'react-icons/hi';
import AprTrendPanel from './AprTrendPanel';

export default function PoolDetailPage({ pools }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const { addAlert, removeAlert, getAlerts, requestNotificationPermission } = useAlerts();
  const [alertThreshold, setAlertThreshold] = useState('');
  const [showAlertInput, setShowAlertInput] = useState(false);

  const pool = pools.find(p => String(p.id) === id || p.poolId === id);

  const poolAlerts = pool ? getAlerts(pool.id) : [];

  const protocolColor = pool ? PROTOCOL_COLORS[pool.protocol] || colors.textPrimary : null;
  const risk = pool ? (RISK_MAP[pool.risk] || { color: '#888', bg: '#f0f0f0', border: '#ccc' }) : null;

  const handleAddAlert = useCallback(() => {
    if (!pool || !alertThreshold) return;
    const val = parseFloat(alertThreshold);
    if (isNaN(val) || val <= 0 || val === pool.apr) return;
    const direction = val > pool.apr ? 'above' : 'below';
    addAlert(pool.id, pool.name, val, direction);
    requestNotificationPermission();
    setAlertThreshold('');
    setShowAlertInput(false);
  }, [pool, alertThreshold, addAlert, requestNotificationPermission]);

  if (!pool) {
    return (
      <div style={{
        padding: 60,
        textAlign: 'center',
        fontFamily: "'IBM Plex Mono', monospace",
        color: colors.textSecondary,
      }}>
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 48, marginBottom: 16 }}>404</div>
        <div style={{ fontSize: 13, marginBottom: 24 }}>Pool not found</div>
        <button
          onClick={() => navigate('/')}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: colors.green,
            background: colors.greenBg,
            border: `1px solid ${colors.greenBorder}`,
            borderRadius: 8,
            padding: '10px 24px',
            cursor: 'pointer',
          }}
        >
          Back to Explorer
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px' }}
    >
      {/* Back + Actions */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 24,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.textSecondary,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px 0',
          }}
        >
          <HiOutlineArrowLeft size={14} />
          Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShareButton pool={pool} colors={colors} />
          <button
            onClick={() => setShowAlertInput(!showAlertInput)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: poolAlerts.length > 0 ? colors.green : colors.textSecondary,
              background: poolAlerts.length > 0 ? colors.greenBg : 'transparent',
              border: `1px solid ${poolAlerts.length > 0 ? colors.greenBorder : colors.border}`,
              borderRadius: 8,
              padding: '6px 12px',
              cursor: 'pointer',
            }}
          >
            <HiOutlineBell size={14} />
            Alert{poolAlerts.length > 0 ? ` (${poolAlerts.length})` : ''}
          </button>
        </div>
      </div>

      {/* Alert input */}
      {showAlertInput && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
          padding: '12px 16px',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
        }}>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary }}>
            Alert when APR reaches:
          </span>
          <input
            type="number"
            value={alertThreshold}
            onChange={e => setAlertThreshold(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddAlert()}
            placeholder={`e.g. ${Math.round(pool.apr * 1.2)}`}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              width: 80,
              padding: '4px 8px',
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              background: colors.bg,
              color: colors.textPrimary,
              outline: 'none',
            }}
          />
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary }}>%</span>
          <button onClick={handleAddAlert} style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 600,
            color: '#FFF', background: colors.green, border: 'none',
            borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
          }}>
            Set
          </button>
          {poolAlerts.map((a, i) => (
            <span key={i} style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              color: a.triggered ? colors.gold : colors.green,
              background: a.triggered ? colors.goldBg : colors.greenBg,
              border: `1px solid ${a.triggered ? colors.goldBorder : colors.greenBorder}`,
              borderRadius: 4, padding: '2px 6px',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              cursor: 'pointer',
            }} onClick={() => removeAlert(a.poolId, a.direction)}>
              {a.direction === 'above' ? '\u2265' : '\u2264'}{a.threshold}% \u2715
            </span>
          ))}
        </div>
      )}

      {/* Hero */}
      <div style={{
        background: colors.surface,
        borderRadius: 14,
        border: `1px solid ${colors.border}`,
        boxShadow: `0 2px 8px ${colors.shadow}`,
        padding: isMobile ? '24px 16px' : '32px',
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <TokenStack tokens={pool.tokens || []} size={isMobile ? 40 : 48} />
          <div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: isMobile ? 24 : 28,
              color: colors.textPrimary,
            }}>
              {pool.name}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.textSecondary,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <ProtocolIcon protocol={pool.protocol} size={14} /> {pool.protocol}
              </span>
              <span style={{ color: colors.labelMuted }}>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <ChainIcon chain={pool.chain} size={14} /> {pool.chain}
              </span>
              {pool.stablecoin && (
                <>
                  <span style={{ color: colors.labelMuted }}>·</span>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    color: colors.green, background: colors.greenBg,
                    border: `1px solid ${colors.greenBorder}`,
                    padding: '1px 6px', borderRadius: 3, fontWeight: 500,
                  }}>STABLE</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* APR hero */}
        <div style={{ textAlign: 'center', padding: '20px 0 24px' }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1.5,
            marginBottom: 8,
          }}>
            ANNUAL PERCENTAGE RATE
          </div>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: isMobile ? 52 : 64,
            color: (pool.apr || 0) > 20 ? colors.gold : colors.green,
            lineHeight: 1,
          }}>
            {(pool.apr || 0).toFixed(2)}%
          </div>
          {(pool.apyBase > 0 || pool.apyReward > 0) && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 16,
              marginTop: 12,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
            }}>
              <span style={{ color: colors.green }}>
                Base: {(pool.apyBase || 0).toFixed(2)}%
              </span>
              {pool.apyReward > 0 && (
                <span style={{ color: colors.gold }}>
                  Reward: +{(pool.apyReward || 0).toFixed(2)}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Metrics grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr 1fr',
        gap: 12,
        marginBottom: 20,
      }}>
        {[
          { label: 'TVL', value: fmt(pool.tvl) },
          { label: 'VOL 24H', value: fmt(pool.volume24h) },
          { label: 'FEES 24H', value: fmt(pool.fees24h) },
          { label: 'FEE TIER', value: pool.fee },
          { label: 'RISK', value: pool.risk, isRisk: true },
        ].map(m => (
          <div key={m.label} style={{
            background: colors.surface,
            borderRadius: 10,
            border: `1px solid ${colors.border}`,
            padding: '14px 16px',
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1.2,
              marginBottom: 6,
            }}>
              {m.label}
            </div>
            {m.isRisk ? (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                color: risk.color,
                background: risk.bg,
                border: `1px solid ${risk.border}`,
                padding: '2px 8px',
                borderRadius: 3,
              }}>
                {m.value}
              </span>
            ) : (
              <div style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 18,
                color: colors.textPrimary,
              }}>
                {m.value}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tokens + APR History side by side on desktop */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '280px 1fr',
        gap: 16,
        marginBottom: 20,
      }}>
        {/* Tokens */}
        <div style={{
          background: colors.surface,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: '20px',
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 12,
          }}>
            TOKENS
          </div>
          {(pool.tokens || []).map(t => (
            <div key={t} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
            }}>
              <TokenIcon symbol={t} size={28} />
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 14,
                color: colors.textPrimary,
                fontWeight: 500,
              }}>{t}</span>
            </div>
          ))}
        </div>

        {/* APR History */}
        <div style={{
          background: colors.surface,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}>
          <AprHistoryChart pool={pool} />
        </div>
      </div>

      {/* APR Trend & Prediction (Pro) */}
      <div style={{ marginBottom: 20 }}>
        <AprTrendPanel pool={pool} />
      </div>

      {/* CTAs */}
      <div style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 40,
      }}>
        {pool.poolId && (
          <a
            href={`https://defillama.com/yields/pool/${pool.poolId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              minWidth: 200,
              display: 'block',
              padding: '14px 0',
              borderRadius: 8,
              background: '#1a5276',
              color: '#FFFFFF',
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            View on DeFi Llama \u2197
          </a>
        )}
        {getPoolUrl(pool) && (
          <a
            href={getPoolUrl(pool)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              minWidth: 200,
              display: 'block',
              padding: '14px 0',
              borderRadius: 8,
              background: protocolColor,
              color: '#FFFFFF',
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.5,
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            Add Liquidity on {pool.protocol} \u2197
          </a>
        )}
        {getSwapUrl(pool) && (
          <a
            href={getSwapUrl(pool)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              minWidth: 200,
              display: 'block',
              padding: '14px 0',
              borderRadius: 8,
              border: `1.5px solid ${protocolColor}`,
              background: 'transparent',
              color: protocolColor,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.5,
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            Swap on {pool.protocol} \u2197
          </a>
        )}
      </div>
    </motion.div>
  );
}
